// «Не попали в табель»: люди «на вахте», у которых назначение кончилось, и продление пачкой.
//
// ## Зачем
//
// 10.10.2026 заказчик понял дату «По» в мастере постановки как «период работы» и закрыл
// назначения сентябрём. Замер боя 19.10.2026: тридцать человек «на вахте» без действующего и
// будущего назначения. Табель строится из назначений, ведомость берёт людей при создании, — в
// октябре этих людей нет нигде. Продлевать по одному из карточек — около ста двадцати нажатий с
// поиском каждого человека.
//
// ## Почему поверх табеля
//
// Табель ведёт сам финансовый директор, итогом по каждому объекту, и это единственный экран,
// который он точно откроет до первой цифры октября. Табель перестраивается из назначений: после
// продления строки появляются в сетке тут же, и часы можно вносить сразу. Ведомость создаётся
// после табеля и возьмёт уже продлённых.
//
// ## Два входа и одно число
//
// С табеля объекта (кнопка в полосе действий) — люди ЭТОГО объекта, у которых назначение кончилось
// раньше конца открытого месяца, и отмечены те из них, у кого оно кончилось в прошлом или в этом
// месяце: человек нажал «Не попали в табель: 2 человека», и переспрашивать его, кого именно,
// незачем. Кончившийся раньше (в июле при октябрьском табеле) заранее НЕ отмечен: продление вернёт
// его во все табели с того дня, а если он правда уезжал, ему нужна новая постановка — об этом
// сказано в строке, и рядом ссылка в карточку. Из «Дел» — все объекты, и не отмечено ничего: там
// их тридцать, и продлить того, кто правда уехал, значило бы завести пустую строку в октябре.
// Число на кнопке, в деле и в первой строке панели считается ОДНОЙ функцией по одному ответу
// сервера: «на кнопке два, в панели три» читалось бы как поломка.
//
// ## Дата «По» одна на пачку и по умолчанию пустая
//
// Пустое поле — «без даты окончания», и это единственный обратимый вариант: «Закрыть» в карточке
// поставит последний день заново. Продление до даты сократить потом нечем.

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  RiAlertLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiUserLine,
} from '@remixicon/react';

import type { BulkExtendOut, OnShiftUnassignedOut } from '@/shared/api/endpoints/people';
import type { ApiError } from '@/shared/errors/api-error';
import { useAuthStore } from '@/shared/auth/store';
import {
  ДАТА_ПО_НЕДОПИСАНА,
  вСозданнуюВедомость,
  давноКончилось,
  датаПоНедописана,
  кончилосьДоПрошлогоМесяца,
  последствиеДатыПо,
  разрывБольшеМесяца,
} from '@/shared/people/последний-день';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch, pushPanel } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import { formatPeriodKey, месяцИзАдреса, type Period } from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { RowCheckbox } from '@/shared/ui/row-checkbox';
import { EmptyState, ErrorState, Skeleton } from '@/shared/ui/screen-state';

import { useExtendAssignmentsBulk, useOnShiftUnassigned } from '../queries';
import { PANEL_TS_UNASSIGNED, TS_UNASSIGNED_ALL } from '../route';

/** Продлевают те же, кто ставит на посты и закрывает назначения: кадры, финотдел, администратор. */
export const ПРАВО_ПРОДЛЕВАТЬ = 'core.employee.edit';

const ЛЮДИ = ['человек', 'человека', 'человек'] as const;
const ОБЪЕКТЫ_НА = ['объекте', 'объектах', 'объектах'] as const;
const НАЗНАЧЕНИЯ = ['назначение', 'назначения', 'назначений'] as const;
const ПОЛЕ_ПО = 'ts-unassigned-date-to';

const НЕТ_ПРАВА =
  'Продлевать назначения могут кадры, финансовый отдел и администратор: нужно право на правку карточек людей. Попросите администратора выдать его.';

// ── Чистые части: их гоняют проверки без экрана ───────────────────────────────────────────

/** Что задано в опознавателе панели: все объекты либо объект и месяц его табеля. */
export interface ОпознавательПанели {
  объект: string | null;
  месяц: Period | null;
}

/** `all` — все объекты; `2026-10~объект` — объект и месяц табеля, откуда открыли. */
export function разобратьОпознаватель(id: string): ОпознавательПанели {
  if (id === TS_UNASSIGNED_ALL || id === '') {
    return { объект: null, месяц: null };
  }
  const волна = id.indexOf('~');
  if (волна === -1) {
    return { объект: null, месяц: месяцИзАдреса(id) };
  }
  const объект = id.slice(волна + 1);
  return { объект: объект === '' ? null : объект, месяц: месяцИзАдреса(id.slice(0, волна)) };
}

/** Опознаватель панели для табеля объекта за месяц. */
export function опознавательОбъекта(siteId: string, месяц: Period): string {
  return `${formatPeriodKey(месяц)}~${siteId}`;
}

/** Последний день месяца ISO-строкой: «2026-10-31». Через UTC, чтобы день не съехал с поясом. */
function последнийДень(месяц: Period): string {
  const дней = new Date(Date.UTC(месяц.year, месяц.month, 0)).getUTCDate();
  return `${formatPeriodKey(месяц)}-${String(дней).padStart(2, '0')}`;
}

/**
 * Кто из списка «не попал в табель» этого месяца: назначение кончилось РАНЬШЕ последнего дня.
 *
 * Без месяца — все строки (так считает дело в «Делах»). С месяцем — только те, у кого последний
 * день раньше конца месяца: человек, кончившийся 30.09, в сентябрьский табель попал целиком, и
 * назвать его там «не попавшим» было бы неправдой. Люди без назначений в этот счёт не входят:
 * объекта у них нет, и ни в каком табеле объекта их не ждут.
 */
export function неПопали(
  строки: readonly OnShiftUnassignedOut[],
  месяц: Period | null,
): OnShiftUnassignedOut[] {
  if (месяц === null) {
    return [...строки];
  }
  const конец = последнийДень(месяц);
  return строки.filter((строка) => строка.date_to !== null && строка.date_to < конец);
}

/**
 * Давно ли кончилось назначение строки. С табеля — раньше прошлого месяца ЭТОГО табеля (для
 * октябрьского — раньше 01.09); из «Дел», где месяца нет, — больше месяца назад от сегодняшнего
 * дня. Такую строку панель заранее не отмечает и называет словами: продление вернёт человека во
 * все табели с того дня, а уезжавшему нужна новая постановка.
 */
export function кончилосьДавно(
  строка: OnShiftUnassignedOut,
  месяц: Period | null,
  сегодня: string,
): boolean {
  if (строка.date_to === null) {
    return false;
  }
  return месяц === null
    ? разрывБольшеМесяца(строка.date_to, сегодня)
    : кончилосьДоПрошлогоМесяца(строка.date_to, месяц);
}

/** Можно ли продлить строку отсюда: есть назначение и сервер не назвал причины отказа. */
export function можноПродлить(строка: OnShiftUnassignedOut): строка is OnShiftUnassignedOut & {
  assignment_id: string;
} {
  return строка.assignment_id !== null && строка.extend_blocked_reason === null;
}

interface Группа {
  ключ: string;
  название: string;
  строки: OnShiftUnassignedOut[];
}

/** Группы по объекту последнего назначения в порядке сервера; без назначений — последней. */
export function поОбъектам(строки: readonly OnShiftUnassignedOut[]): Группа[] {
  const группы = new Map<string, Группа>();
  for (const строка of строки) {
    const ключ = строка.site_id ?? 'без-назначений';
    const группа = группы.get(ключ) ?? {
      ключ,
      название: строка.site_name ?? 'Назначений не было',
      строки: [],
    };
    группа.строки.push(строка);
    группы.set(ключ, группа);
  }
  return [...группы.values()].sort(
    (а, б) => Number(а.ключ === 'без-назначений') - Number(б.ключ === 'без-назначений'),
  );
}

// ── Кнопка в полосе действий табеля ───────────────────────────────────────────────────────

/**
 * «Не попали в табель: 2 человека» — вход в панель с табеля объекта.
 *
 * Стоит в УЖЕ существующей полосе действий, а не новой строкой над сеткой: строка, приехавшая
 * позже сетки, сдвинула бы ячейку под курсором. Запрос списка начинается вместе с запросом
 * сетки (`TimesheetScreen` зовёт тот же хук), поэтому к первому кадру сетки число обычно уже
 * есть. Без права продлевать кнопки нет вовсе: вход вёл бы в отказ, а список закрыт тем же
 * правом.
 */
export function КнопкаНеПопалиВТабель({ siteId, месяц }: { siteId: string; месяц: Period }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, ПРАВО_ПРОДЛЕВАТЬ);
  const список = useOnShiftUnassigned(siteId, можно);
  const navigate = useNavigate();
  const location = useLocation();

  const кто = неПопали(список.data?.items ?? [], месяц);
  if (!можно || кто.length === 0) {
    return null;
  }
  return (
    <Button.Root
      type='button'
      variant='neutral'
      mode='stroke'
      size='xsmall'
      onClick={() => {
        const следующее = pushPanel(parseSearch(location.search), {
          view: PANEL_TS_UNASSIGNED,
          id: опознавательОбъекта(siteId, месяц),
        });
        void navigate(`${location.pathname}${buildSearch(следующее)}`);
      }}
    >
      <Button.Icon as={RiUserLine} />
      Не попали в табель: {countLabel(кто.length, ЛЮДИ)}
    </Button.Root>
  );
}

// ── Панель ────────────────────────────────────────────────────────────────────────────────

export function НеПопалиВТабель({ id }: { id: string }) {
  const { объект, месяц } = разобратьОпознаватель(id);
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, ПРАВО_ПРОДЛЕВАТЬ);
  const список = useOnShiftUnassigned(объект, можно);
  const продление = useExtendAssignmentsBulk();

  const [выбор, setВыбор] = useState<ReadonlySet<string>>(() => new Set());
  // Отметки с табеля объекта ставятся ОДИН раз, когда список приехал; дальше они принадлежат
  // человеку, и обновление списка их не трогает (тот же приём, что у «Создать ведомость»).
  const [предвыборПрименён, setПредвыборПрименён] = useState(false);
  const [датаПо, setДатаПо] = useState('');
  // Набранное в поле «По», пока дата не дописана: поле отдаёт наружу пустое и на недонабранную
  // дату, а пустое здесь значит «без даты окончания».
  const [набрано, setНабрано] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [итог, setИтог] = useState<BulkExtendOut | null>(null);

  const строки = список.data === undefined ? null : неПопали(список.data.items, месяц);
  const продлеваемые = (строки ?? []).filter(можноПродлить);
  const сегодня = todayIso();

  if (!предвыборПрименён && строки !== null) {
    setПредвыборПрименён(true);
    if (объект !== null) {
      setВыбор(
        new Set(
          продлеваемые
            .filter((строка) => !кончилосьДавно(строка, месяц, сегодня))
            .map((строка) => строка.assignment_id),
        ),
      );
    }
  }

  // Отмечено считается по тем, кого ещё можно продлить: продлённые уходят из списка, и
  // оставшиеся в памяти отметки не должны попасть ни в счёт, ни в запрос.
  const отмеченные = продлеваемые.filter((строка) => выбор.has(строка.assignment_id));
  const всеОтмечены = продлеваемые.length > 0 && отмеченные.length === продлеваемые.length;

  function отметить(назначение: string) {
    setЗапрет(null);
    setВыбор((было) => {
      const стало = new Set(было);
      if (стало.has(назначение)) {
        стало.delete(назначение);
      } else {
        стало.add(назначение);
      }
      return стало;
    });
  }

  function продлить() {
    if (!можно) {
      setЗапрет(НЕТ_ПРАВА);
      return;
    }
    if (продление.isPending) {
      setЗапрет('Уже продлеваем, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (отмеченные.length === 0) {
      setЗапрет('Отметьте хотя бы одного человека: продлеваются только отмеченные.');
      return;
    }
    if (датаПоНедописана(датаПо, набрано)) {
      setЗапрет(ДАТА_ПО_НЕДОПИСАНА);
      return;
    }
    setЗапрет(null);
    продление.mutate(
      {
        assignment_ids: отмеченные.map((строка) => строка.assignment_id),
        date_to: датаПо === '' ? null : датаПо,
      },
      { onSuccess: setИтог },
    );
  }

  if (!можно) {
    return (
      <Alert.Root variant='light' status='warning' size='small' role='status'>
        <Alert.Icon as={RiErrorWarningLine} />
        <span>{НЕТ_ПРАВА}</span>
      </Alert.Root>
    );
  }

  const последствие = последствиеДатыПо(
    датаПо,
    отмеченные.length === 1 ? 'человек' : 'люди постов',
  );

  return (
    <div className='flex min-h-full flex-col gap-4'>
      {/* Первой строкой, до списка, — сколько и где. Из «Дел» под панелью открывается табель
          первого объекта, и без этой строки казалось бы, что попал не туда. */}
      {строки !== null && строки.length > 0 && (
        <p className='text-label-sm text-text-strong-950'>{сколькоИГде(строки, объект)}</p>
      )}
      <p className='text-paragraph-sm text-text-sub-600'>
        Назначение у них кончилось или его не было, а в карточке «на вахте»: в табеле их нет, и в
        ведомость их не возьмут, пока назначение не продлят или человека не поставят на пост. Если у
        человека вахта правда кончилась, смените ему состояние в реестре людей.
      </p>

      {список.error !== null && список.data === undefined ? (
        <ErrorState
          subject='список людей без поста'
          error={список.error as ApiError}
          onRetry={() => void список.refetch()}
        />
      ) : строки === null ? (
        <div aria-hidden='true' className='flex flex-col gap-1'>
          {Array.from({ length: 5 }, (_нет, номер) => (
            <Skeleton key={номер} className='h-11 w-full rounded-lg' />
          ))}
        </div>
      ) : строки.length === 0 ? (
        <EmptyState
          icon={RiCheckboxCircleLine}
          title={
            объект === null
              ? 'Все люди на вахте стоят на постах'
              : 'На этом объекте все люди на вахте стоят на постах'
          }
          description={
            объект === null
              ? 'Ни у кого в состоянии «на вахте» назначение не кончилось: табель и ведомость их возьмут.'
              : 'Ни у кого из них назначение не кончилось раньше конца месяца: все они в табеле.'
          }
        />
      ) : (
        <div className='flex flex-col gap-3'>
          {продлеваемые.length > 0 && (
            <div>
              <LinkButton.Root
                type='button'
                variant='primary'
                size='medium'
                underline
                onClick={() => {
                  setЗапрет(null);
                  setВыбор(
                    всеОтмечены
                      ? new Set()
                      : new Set(продлеваемые.map((строка) => строка.assignment_id)),
                  );
                }}
              >
                {всеОтмечены ? 'Снять отметки' : `Отметить всех (${продлеваемые.length})`}
              </LinkButton.Root>
            </div>
          )}
          {поОбъектам(строки).map((группа) => (
            // Группа, а не `section` с подписью: так размечены панели стопки, и сквозные
            // проверки берут последнюю такую как открытую панель (`e2e/панели.spec.ts`).
            <div
              key={группа.ключ}
              role='group'
              aria-label={группа.название}
              className='flex flex-col'
            >
              <h3 className='pb-1 text-subheading-xs uppercase text-text-sub-600'>
                {группа.название}
              </h3>
              <ul className='flex flex-col'>
                {группа.строки.map((строка) => (
                  <СтрокаЧеловека
                    key={строка.employee_id}
                    строка={строка}
                    давно={кончилосьДавно(строка, месяц, сегодня)}
                    отмечен={строка.assignment_id !== null && выбор.has(строка.assignment_id)}
                    onToggle={() => {
                      if (строка.assignment_id !== null) {
                        отметить(строка.assignment_id);
                      }
                    }}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Итог стоит ВНЕ блока списка и держится до закрытия панели: после продления список
          пустеет, и пустое состояние «все на постах» не должно съесть строки о пропущенных. */}
      {итог !== null && <ИтогПродления итог={итог} />}

      {продлеваемые.length > 0 && (
        <div
          role='group'
          aria-label='Продление отмеченных'
          className='sticky -bottom-4 z-10 -mx-4 -mb-4 mt-auto flex flex-col gap-2 border-t border-stroke-soft-200 bg-bg-white-0 px-4 py-3 тач:безопасно-снизу'
        >
          {/* Обёртка ловит набор в поле: поле само отдаёт наружу только целую дату. */}
          <div onInput={(event) => setНабрано((event.target as HTMLInputElement).value)}>
            <DateField
              id={ПОЛЕ_ПО}
              label='По'
              value={датаПо}
              onChange={setДатаПо}
              hint={
                последствие === null
                  ? 'Пустое поле значит «без даты окончания»: так продление можно отыграть кнопкой «Закрыть» в карточке.'
                  : `${последствие} Сдвинуть этот день раньше потом будет нечем.`
              }
            />
          </div>
          {продление.error !== null && <ErrorAlert error={продление.error} />}
          {запрет !== null && (
            <Hint.Root hasError role='alert'>
              {запрет}
            </Hint.Root>
          )}
          <div className='flex items-center justify-end'>
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='small'
              onClick={продлить}
            >
              {продление.isPending
                ? 'Продлеваем…'
                : отмеченные.length === 0
                  ? 'Продлить отмеченных'
                  : `Продлить ${countLabel(отмеченные.length, НАЗНАЧЕНИЯ)}`}
            </Button.Root>
          </div>
        </div>
      )}
    </div>
  );
}

/** «Люди на вахте без поста: 30 человек на 12 объектах» либо про один объект. */
function сколькоИГде(строки: readonly OnShiftUnassignedOut[], объект: string | null): string {
  if (объект !== null) {
    return `Не попали в табель этого месяца: ${countLabel(строки.length, ЛЮДИ)}`;
  }
  const объектов = new Set(строки.map((строка) => строка.site_id).filter((код) => код !== null))
    .size;
  const где = объектов > 0 ? ` на ${countLabel(объектов, ОБЪЕКТЫ_НА)}` : '';
  return `Люди на вахте без поста: ${countLabel(строки.length, ЛЮДИ)}${где}`;
}

/** Адрес вкладки «Назначения» карточки человека: там ставят на пост и продлевают по одному. */
function адресКарточки(строка: OnShiftUnassignedOut): string {
  return `/people/employees/r/${encodeURIComponent(строка.employee_id)}?tab=assignments`;
}

/**
 * Строка человека. Нажимается вся строка: фамилия тоже часть галочки.
 *
 * Где продлить нельзя (назначений не было, объект закрыт, пост снят), галочки нет, а есть
 * причина и ссылка в карточку: там человека ставят на действующий пост. Серой галочки здесь нет
 * намеренно — она не довела бы нажатие ни до чего и не сказала бы почему. Давно кончившееся
 * (`давно`) отметить можно, но под фамилией сказано, что продление вернёт человека во все табели
 * с того дня, и рядом ссылка в карточку — поставить заново с нужного дня.
 */
function СтрокаЧеловека({
  строка,
  давно,
  отмечен,
  onToggle,
}: {
  строка: OnShiftUnassignedOut;
  давно: boolean;
  отмечен: boolean;
  onToggle: () => void;
}) {
  const где = [
    строка.post_name,
    строка.date_to === null ? null : `по ${formatDateOnly(строка.date_to)}`,
  ]
    .filter((часть) => часть !== null)
    .join(' · ');

  if (!можноПродлить(строка)) {
    return (
      <li className='flex min-h-11 items-start gap-2 border-b border-stroke-soft-200 px-1 py-1.5 last:border-b-0'>
        <span className='flex size-8 shrink-0 items-center justify-center тач:size-11'>
          <RiAlertLine className='size-4 text-warning-base' aria-hidden />
        </span>
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='break-words text-label-sm text-text-strong-950'>
            {строка.employee_name}
          </span>
          <span className='break-words text-paragraph-xs text-text-sub-600'>
            {[где, строка.extend_blocked_reason].filter((часть) => часть).join(' · ')}
          </span>
          <Link
            to={адресКарточки(строка)}
            className='text-paragraph-xs text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
          >
            {строка.next_action ?? 'Открыть карточку'}
          </Link>
        </span>
      </li>
    );
  }

  const код = `ts-unassigned-${строка.assignment_id}`;
  return (
    <li className='border-b border-stroke-soft-200 last:border-b-0'>
      <label
        htmlFor={код}
        className='flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-1 transition duration-200 ease-out hover:bg-bg-weak-50'
      >
        <RowCheckbox
          id={код}
          label={`Отметить: ${строка.employee_name}`}
          checked={отмечен}
          onCheckedChange={onToggle}
        />
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='break-words text-label-sm text-text-strong-950'>
            {строка.employee_name}
          </span>
          <span className='break-words text-paragraph-xs text-text-sub-600'>{где}</span>
          {давно && строка.date_to !== null && (
            // Тёмный оттенок, а не базовый: базовый оранжевый мелким текстом на белом не
            // читается (контраст ниже 4,5, `shared/ui/badge`).
            <span className='break-words text-paragraph-xs text-warning-dark'>
              {давноКончилось(строка.date_to)}
            </span>
          )}
        </span>
      </label>
      {/* Ссылка ВНЕ подписи галочки: нажатие по ней уводит в карточку, а не ставит отметку.
          Отступ — ширина галочки и зазора, чтобы ссылка стояла под фамилией. */}
      {давно && (
        <div className='pb-1.5 pl-11 тач:pl-14'>
          <Link
            to={адресКарточки(строка)}
            className='text-paragraph-xs text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
          >
            Открыть карточку и поставить заново
          </Link>
        </div>
      )}
    </li>
  );
}

/** Итог пачки: сколько продлено и кто пропущен, поимённо и с причиной. */
function ИтогПродления({ итог }: { итог: BulkExtendOut }) {
  return (
    <div role='status' className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-4 py-3'>
      <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' aria-hidden />
        {итог.summary.charAt(0).toUpperCase() + итог.summary.slice(1)}
      </span>
      {итог.extended > 0 && (
        <span className='text-paragraph-xs text-text-sub-600'>
          Продлённые уже в табеле: внесите им часы. {вСозданнуюВедомость('люди')}
        </span>
      )}
      {итог.skipped.map((пропуск) => (
        <span
          key={пропуск.assignment_id}
          className='flex items-start gap-1.5 text-paragraph-xs text-text-sub-600'
        >
          <RiAlertLine className='mt-0.5 size-4 shrink-0 text-error-base' aria-hidden />
          <span>
            <span className='text-text-strong-950'>{пропуск.label}</span>: {пропуск.reason}
          </span>
        </span>
      ))}
    </div>
  );
}
